import { ThumbnailInfo } from '@app/core/hooks/api/useApiThumbnailsRange';
import { calculateThumbnailDimensions } from '@app/core/thumbnails/utils';
import { useMemo } from 'react';
import { getFirstTickDistance, getNumberOfTicks, getTickDistance } from './utils';

interface ThumbnailLayout {
  thumbnail: ThumbnailInfo;
  width: number;
  x: number;
  spriteWidth: number;
}

interface UseThumbnailLayoutParams {
  thumbnails: ThumbnailInfo[];
  streamEl: HTMLDivElement;
  thumbHeight: number;
  containerWidth: number;
}

export function useThumbnailLayout({
  thumbnails,
  streamEl,
  thumbHeight,
  containerWidth,
}: UseThumbnailLayoutParams): ThumbnailLayout[] {
  const firstTickDistance = getFirstTickDistance(streamEl) || 0;
  const tickDistance = getTickDistance(streamEl) || 0;
  const numberOfTicks = getNumberOfTicks(streamEl);

  return useMemo(() => {
    // Select thumbnails based on tick count
    const step = Math.floor(thumbnails.length / numberOfTicks);
    const selectedThumbnails = thumbnails
      .filter((_, index) => index % step === 0)
      .slice(0, numberOfTicks + 1);

    const getThumbPosition = (index: number) => {
      if (index === 0) return 0;
      return (index - 1) * tickDistance + (firstTickDistance + 15);
    };

    const getThumbWidth = (index: number, totalThumbnails: number) => {
      const baseWidth = index === 0 ? firstTickDistance + 14 : tickDistance;

      // For the last thumbnail, ensure it doesn't extend beyond container width
      if (index === totalThumbnails - 1 && containerWidth > 0) {
        const position = getThumbPosition(index);
        const maxWidth = Math.max(0, containerWidth - position);
        return Math.min(baseWidth, maxWidth);
      }

      return baseWidth;
    };

    return selectedThumbnails.map((thumbnail, index) => {
      const width = getThumbWidth(index, selectedThumbnails.length);
      const x = getThumbPosition(index);
      const { spriteWidth } = calculateThumbnailDimensions(thumbnail.metadata, thumbHeight);

      return {
        thumbnail,
        width,
        x,
        spriteWidth,
      };
    });
  }, [thumbnails, numberOfTicks, tickDistance, firstTickDistance, containerWidth, thumbHeight]);
}
